import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  ArrowUpDown,
  Box,
  Building2,
  Calculator,
  ClipboardList,
  CreditCard,
  Database,
  DollarSign,
  FileStack,
  FileText,
  Folder,
  FolderOpen,
  GitBranch,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  Car,
  CalendarClock,
  ListOrdered,
  Navigation,
  Package,
  Package2,
  Route,
  Ruler,
  ScrollText,
  Settings,
  Shield,
  ShoppingCart,
  Sigma,
  HardHat,
  LandPlot,
  Sprout,
  Store,
  Truck,
  Users,
  Wallet,
  Warehouse,
  Wrench,
  Link,
} from "lucide-react";

export interface SubmoduleDef {
  id: string;
  name: string;
  icon: LucideIcon;
}

export interface MasterGroupDef {
  id: string;
  name: string;
  icon: LucideIcon;
  submodules: SubmoduleDef[];
}

export interface ModuleDef {
  id: string;
  name: string;
  icon: LucideIcon;
  iconBadgeClass?: string;
  hasSubmodules: boolean;
  submodules?: SubmoduleDef[];
  masterGroups?: MasterGroupDef[];
}

const mastersGroup = (
  id: string,
  submodules: SubmoduleDef[],
): MasterGroupDef => ({
  id,
  name: "Maestros",
  icon: Folder,
  submodules,
});

export const modules: ModuleDef[] = [
  {
    id: "dashboard",
    name: "Dashboard",
    icon: LayoutDashboard,
    iconBadgeClass: "bg-indigo-500 text-white",
    hasSubmodules: false,
  },
  {
    id: "configuration",
    name: "Configuración",
    icon: Settings,
    iconBadgeClass: "bg-slate-600 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "cost-centers", name: "Centros de costo", icon: Wallet },
      { id: "plant-types", name: "Tipos de centro", icon: Database },
      { id: "companies", name: "Sociedades", icon: Building2 },
      { id: "approval-flows", name: "Flujos de aprobación", icon: GitBranch },
    ],
  },
  {
    id: "purchasing",
    name: "Adquisiciones",
    icon: ShoppingCart,
    iconBadgeClass: "bg-amber-600 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "purchase-orders", name: "Órdenes de compra", icon: FileText },
      {
        id: "material-requests",
        name: "Solicitudes de pedido",
        icon: FileText,
      },
    ],
    masterGroups: [
      mastersGroup("purchasing-masters", [
        {
          id: "payment-terms",
          name: "Condiciones de pago",
          icon: CalendarClock,
        },
        {
          id: "document-classes",
          name: "Clases de documento",
          icon: FileStack,
        },
        { id: "vendors", name: "Proveedores", icon: Truck },
        { id: "vendor-services", name: "Servicios de proveedor", icon: Wrench },
      ]),
    ],
  },
  {
    id: "sales",
    name: "Ventas",
    icon: Store,
    iconBadgeClass: "bg-violet-600 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "sales-orders", name: "Órdenes de venta", icon: FileText },
    ],
    masterGroups: [
      mastersGroup("sales-masters", [
        { id: "customers", name: "Clientes", icon: Users },
      ]),
    ],
  },
  {
    id: "mm",
    name: "Movimiento de materiales",
    icon: Package,
    iconBadgeClass: "bg-emerald-500 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "material-movements", name: "Movimientos", icon: ArrowUpDown },
      { id: "dispatch-guides", name: "Detalles de movimientos", icon: Truck },
      { id: "stock", name: "Consulta de stock", icon: Layers },
    ],
    masterGroups: [
      mastersGroup("mm-masters", [
        { id: "materials", name: "Materiales", icon: Box },
        {
          id: "material-unit-conversions",
          name: "Conversión unidades",
          icon: ArrowLeftRight,
        },
        { id: "material-groups", name: "Grupos de materiales", icon: Package },
        { id: "batches", name: "Lotes", icon: Package2 },
        {
          id: "batch-templates",
          name: "Plantillas de lote",
          icon: LayoutTemplate,
        },
        { id: "units", name: "Unidades de medida", icon: Ruler },
        { id: "plants", name: "Centros", icon: Building2 },
        { id: "storage-locations", name: "Almacenes", icon: Warehouse },
        {
          id: "storage-loc-templates",
          name: "Ubicaciones",
          icon: LayoutTemplate,
        },
        {
          id: "movement-types",
          name: "Tipos de movimiento",
          icon: ArrowUpDown,
        },
        {
          id: "dispatch-guide-statuses",
          name: "Estados de detalle",
          icon: ListOrdered,
        },
      ]),
    ],
  },
  {
    id: "agricola",
    name: "Agrícola",
    icon: Sprout,
    iconBadgeClass: "bg-lime-600 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "labor-tickets", name: "Tarja diaria", icon: ClipboardList },
      { id: "attendance", name: "Asistencia", icon: ClipboardList },
    ],
    masterGroups: [
      mastersGroup("agricola-masters", [
        { id: "fields", name: "Campos", icon: Sprout },
        { id: "cuarteles", name: "Cuarteles", icon: LandPlot },
        { id: "especies", name: "Especies", icon: Sprout },
        { id: "variedades", name: "Variedades", icon: Layers },
        { id: "employees", name: "Empleados", icon: HardHat },
        { id: "labor-activities", name: "Labores", icon: CalendarClock },
        { id: "labor-rates", name: "Tarifas Proveedores", icon: DollarSign },
        {
          id: "labor-rate-generals",
          name: "Tarifas generales",
          icon: DollarSign,
        },
        { id: "labor-retention", name: "Retención", icon: DollarSign },
      ]),
    ],
  },

  {
    id: "logistica",
    name: "Logística",
    icon: Navigation,
    iconBadgeClass: "bg-cyan-600 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "trips", name: "Viajes", icon: Navigation },
      { id: "routes", name: "Rutas", icon: Route },
      {
        id: "vehicle-assignments",
        name: "Asignaciones de vehículos",
        icon: Link,
      },
    ],
    masterGroups: [
      mastersGroup("logistica-masters", [
        { id: "carriers", name: "Transportistas", icon: Truck },
        { id: "drivers", name: "Choferes", icon: Users },
        { id: "vehicles", name: "Vehículos", icon: Car },
        { id: "carrier-types", name: "Tipos de transportes", icon: Truck },
        {
          id: "dispatch-flow-profiles",
          name: "Perfiles de flujo de despacho",
          icon: GitBranch,
        },
      ]),
    ],
  },
  {
    id: "industrial",
    name: "Industrial",
    icon: Calculator,
    iconBadgeClass: "bg-orange-500 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "work-orders", name: "Órdenes de trabajo", icon: ClipboardList },
    ],
    masterGroups: [
      mastersGroup("industrial-masters", [
        {
          id: "work-order-templates",
          name: "Flujos de Trabajo",
          icon: LayoutTemplate,
        },
        {
          id: "work-order-activities",
          name: "Maestro de actividades",
          icon: ListOrdered,
        },
        {
          id: "industrial-formulas",
          name: "Recetas",
          icon: Sigma,
        },
        {
          id: "production-orders",
          name: "Órdenes de producción",
          icon: FolderOpen,
        },
      ]),
    ],
  },
  {
    id: "users-section",
    name: "Estructura organizacional",
    icon: Users,
    iconBadgeClass: "bg-teal-500 text-white",
    hasSubmodules: true,
    masterGroups: [
      mastersGroup("users-masters", [
        { id: "users", name: "Usuarios", icon: Users },
        { id: "roles", name: "Roles", icon: Shield },
        {
          id: "org-positions",
          name: "Puestos de trabajo",
          icon: ListOrdered,
        },
      ]),
    ],
  },
  {
    id: "audit-section",
    name: "Auditoría",
    icon: ScrollText,
    iconBadgeClass: "bg-gray-500 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "audit", name: "Registro de cambios", icon: ScrollText },
    ],
  },
];

export const SUBTENANT_HIDDEN_SUBMODULES: Record<string, string[]> = {
  "users-section": ["users"],
};

export const platformModules: ModuleDef[] = [
  {
    id: "dashboard",
    name: "Dashboard",
    icon: LayoutDashboard,
    iconBadgeClass: "bg-indigo-500 text-white",
    hasSubmodules: false,
  },
  {
    id: "platform-subscriptions",
    name: "Suscripciones",
    icon: CreditCard,
    iconBadgeClass: "bg-purple-500 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "subscriptions", name: "Suscripciones", icon: CreditCard },
      { id: "plans", name: "Planes", icon: ClipboardList },
      { id: "plan-prices", name: "Precios de plan", icon: DollarSign },
      { id: "invoices", name: "Facturas", icon: FileText },
    ],
  },
  {
    id: "platform-masters",
    name: "Maestros",
    icon: Database,
    iconBadgeClass: "bg-blue-500 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "currencies", name: "Monedas", icon: DollarSign },
      { id: "units-core", name: "Unidades de medida", icon: Ruler },
    ],
  },
  {
    id: "users-section",
    name: "Estructura organizacional",
    icon: Users,
    iconBadgeClass: "bg-teal-500 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "users", name: "Usuarios", icon: Users },
      { id: "roles", name: "Roles", icon: Shield },
    ],
  },
  {
    id: "platform-audit",
    name: "Auditoría",
    icon: ScrollText,
    iconBadgeClass: "bg-gray-500 text-white",
    hasSubmodules: true,
    submodules: [
      { id: "audit", name: "Registro de cambios", icon: ScrollText },
    ],
  },
];
